import { Close } from '@carbon/icons-react';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { Dialog, IconButton, makeStyles } from '@material-ui/core';
import AppConfig from '@config';
import { KakaoImage, NaverBandImage, FacebookImage } from '@assets';
import { colorSet, useSnackbar } from '@components/Provider';
import { useContext, useLayoutEffect } from 'react';
import errorMessage from '@utils/errorMessage';
import {
  CompanyInfoContext,
  CompanyInfoContextValue
} from '../context/CompanyInfoContext';

interface Props {
  open: boolean;
  onClose: () => void;
}
const CompanySharedLinkModal = ({ open, onClose }: Props) => {
  const classes = useStyles();
  const context = useContext<CompanyInfoContextValue | null>(
    CompanyInfoContext
  );

  const { partnerInfo } = context || {};

  const partnerId = partnerInfo?.id;

  const { enqueueSnackbar } = useSnackbar();

  const link = `${AppConfig.baseUrl}/companyInfo?partnerId=${partnerId}`;

  const shareLink = (type: 'facebook' | 'kakao' | 'band' | 'default') => {
    switch (type) {
      case 'facebook': {
        window.open(
          'http://www.facebook.com/sharer.php?u=' + encodeURIComponent(link)
        );

        return;
      }

      case 'band': {
        window.open(
          'http://band.us/plugin/share?body=' + encodeURIComponent(link)
        );

        return;
      }

      case 'kakao': {
        if (typeof window.Kakao === 'undefined') {
          return enqueueSnackbar(`${errorMessage.service}`, {
            variant: 'error'
          });
        }

        window.Kakao.Link.sendScrap({
          requestUrl: link
        });

        return;
      }

      default: {
        window.navigator.clipboard.writeText(
          `${AppConfig.baseUrl}/companyInfo?partnerId=${partnerId}`
        );

        return enqueueSnackbar('링크가 복사되었습니다.', {
          variant: 'success'
        });
      }
    }
  };

  useLayoutEffect(() => {
    if (typeof window.Kakao === 'undefined') return;

    const isInitialized = window?.Kakao?.isInitialized();

    if (isInitialized) return;

    window.Kakao.init(process.env.REACT_APP_KAKAO_API_KEY); // 내 웹 키를 할당하면 된다
  }, []);

  return (
    <Dialog
      fullWidth
      classes={{
        paper: classes.root
      }}
      open={open}
      onClose={onClose}
    >
      <div className={classes.title}>
        <CustomizedTypography
          className="main_title"
          variant="h4"
          color="textPrimary"
          fontWeight="bold"
        >
          공유하기
        </CustomizedTypography>

        <IconButton onClick={onClose} className={classes.close}>
          <Close size={24} color={colorSet.gray900} />
        </IconButton>
      </div>
      <div className={classes.cotentsWrapper}>
        <CustomizedTypography
          variant="body2"
          color="textPrimary"
          fontWeight="500"
          gutterBottoms={12}
        >
          우리 회사 프로필을 SNS에서 손쉽게 공유하세요.
        </CustomizedTypography>
        <div className={classes.content}>
          <div
            className="item"
            onClick={() => {
              shareLink('kakao');
            }}
          >
            <img src={KakaoImage} alt="" />
            <CustomizedTypography variant="caption">
              카카오톡
            </CustomizedTypography>
          </div>

          <div
            className="item"
            onClick={() => {
              shareLink('band');
            }}
          >
            <img src={NaverBandImage} alt="" />
            <CustomizedTypography variant="caption">
              네이버 밴드
            </CustomizedTypography>
          </div>

          <div
            className="item"
            onClick={() => {
              shareLink('facebook');
            }}
          >
            <img src={FacebookImage} alt="" />
            <CustomizedTypography variant="caption">
              페이스북
            </CustomizedTypography>
          </div>
        </div>

        <div className={classes.clipboard}>
          <CustomizedTypography
            className="link"
            variant="body2"
            color="textPrimary"
          >
            {link}
          </CustomizedTypography>

          <CustomizedButton
            className="button"
            color="default"
            variant="contained"
            onClick={() => shareLink('default')}
          >
            URL 복사
          </CustomizedButton>
        </div>
      </div>
    </Dialog>
  );
};

export default CompanySharedLinkModal;

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 343,
    padding: 0,
    borderRadius: 16
  },
  close: {
    position: 'absolute',
    top: 18,
    right: 18,
    padding: 6
  },
  title: {
    padding: '22px 22px',
    '& .main_title': {
      padding: '6px 0px'
    }
  },
  cotentsWrapper: {
    padding: '0px 24px 24px'
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 0px 12px 0px',
    marginBottom: 12,
    borderRadius: 8,
    backgroundColor: colorSet.gray100,
    '& .item': {
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      '& img': {
        marginBottom: 4,
        width: 32,
        height: 32
      }
    },
    '&>:not(:last-child)': {
      marginRight: 32
    }
  },
  clipboard: {
    display: 'flex',
    alignItems: 'center',
    '& .link': {
      maxWidth: 208,
      whiteSpace: 'pre',
      borderRadius: 8,
      textOverflow: 'ellipsis',
      backgroundColor: colorSet.gray100,
      overflow: 'hidden',
      padding: '8px 10px',
      marginRight: 4
    },
    '& .button': {
      height: 36,
      minWidth: 84,
      padding: '6px 9px'
    }
  }
}));
